"use client";

import { ChartSvg } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { CHART_INK } from "@/lib/tool-kit/palette";
import {
  BAND_EDGES,
  type BandId,
  SPECTRUM_LOG_RANGE,
  spectrumPosition,
  wavelengthToRgb,
} from "@/lib/tool-kit/physics-waves/electromagnetic";

export interface SpectrumStrings {
  bandNames: Record<BandId, string>;
  figureLabel: string;
  figureDescription: string;
}

const WIDTH = 640;
const HEIGHT = 150;
const LEFT = 20;
const BAR_WIDTH = 600;
const BAR_Y = 44;
const BAR_HEIGHT = 34;

const FILLS: Record<Exclude<BandId, "visible">, string> = {
  gamma: "#5b4a8a",
  xray: "#3b6fb6",
  ultraviolet: "#8b5fbf",
  infrared: "#b4493a",
  microwave: "#d68a27",
  radio: "#4a9a68",
};

const SEGMENTS: { band: BandId; from: number; to: number }[] = [
  {
    band: "gamma",
    from: 10 ** SPECTRUM_LOG_RANGE[0],
    to: BAND_EDGES.gammaXray,
  },
  { band: "xray", from: BAND_EDGES.gammaXray, to: BAND_EDGES.xrayUv },
  { band: "ultraviolet", from: BAND_EDGES.xrayUv, to: BAND_EDGES.uvVisible },
  { band: "visible", from: BAND_EDGES.uvVisible, to: BAND_EDGES.visibleIr },
  { band: "infrared", from: BAND_EDGES.visibleIr, to: BAND_EDGES.irMicrowave },
  {
    band: "microwave",
    from: BAND_EDGES.irMicrowave,
    to: BAND_EDGES.microwaveRadio,
  },
  {
    band: "radio",
    from: BAND_EDGES.microwaveRadio,
    to: 10 ** SPECTRUM_LOG_RANGE[1],
  },
];

/** Decade labels along the bar: number and unit only, so no words to translate. */
const TICKS: [number, string][] = [
  [-12, "1 pm"],
  [-9, "1 nm"],
  [-6, "1 µm"],
  [-3, "1 mm"],
  [0, "1 m"],
  [3, "1 km"],
];

const x = (wavelength: number) =>
  LEFT + BAR_WIDTH * spectrumPosition(wavelength);

/** The whole spectrum on a logarithmic wavelength scale with a marker at one wavelength. */
export function SpectrumBar({
  wavelength,
  strings,
}: {
  wavelength: number;
  strings: SpectrumStrings;
}) {
  const rainbow = Array.from({ length: 8 }, (_, i) => {
    const nm = 380 + ((750 - 380) * i) / 7;
    return { offset: i / 7, color: wavelengthToRgb(nm) ?? "#000" };
  });
  const marker = x(wavelength);
  return (
    <ChartSvg
      className="mx-auto block h-auto w-full max-w-2xl"
      description={strings.figureDescription}
      height={HEIGHT}
      id="wf-spectrum"
      label={strings.figureLabel}
      width={WIDTH}
    >
      <defs>
        <linearGradient id="wf-rainbow" x1="0" x2="1" y1="0" y2="0">
          {rainbow.map((stop) => (
            <stop
              key={stop.offset}
              offset={stop.offset}
              stopColor={stop.color}
            />
          ))}
        </linearGradient>
      </defs>
      {SEGMENTS.map((segment) => {
        const left = x(segment.from);
        const width = x(segment.to) - left;
        const visible = segment.band === "visible";
        return (
          <g key={segment.band}>
            <rect
              fill={
                visible
                  ? "url(#wf-rainbow)"
                  : FILLS[segment.band as Exclude<BandId, "visible">]
              }
              height={BAR_HEIGHT}
              width={px(Math.max(width, 2))}
              x={px(left)}
              y={BAR_Y}
            />
            <text
              fill={visible ? CHART_INK.text : "#ffffff"}
              fontSize={visible ? 11 : 12}
              fontWeight={600}
              textAnchor="middle"
              x={px(left + width / 2)}
              y={visible ? BAR_Y - 8 : BAR_Y + BAR_HEIGHT / 2 + 4}
            >
              {strings.bandNames[segment.band]}
            </text>
          </g>
        );
      })}
      {TICKS.map(([decade, label]) => {
        const at = x(10 ** decade);
        return (
          <g key={label}>
            <line
              stroke={CHART_INK.axis}
              x1={at}
              x2={at}
              y1={BAR_Y + BAR_HEIGHT}
              y2={BAR_Y + BAR_HEIGHT + 6}
            />
            <text
              fill={CHART_INK.muted}
              fontSize={11}
              textAnchor="middle"
              x={at}
              y={BAR_Y + BAR_HEIGHT + 36}
            >
              {label}
            </text>
          </g>
        );
      })}
      <line
        stroke={CHART_INK.text}
        strokeWidth={2}
        x1={px(marker)}
        x2={px(marker)}
        y1={BAR_Y - 2}
        y2={BAR_Y + BAR_HEIGHT + 2}
      />
      <polygon
        fill={CHART_INK.text}
        points={`${px(marker - 7)},${BAR_Y + BAR_HEIGHT + 20} ${px(marker + 7)},${BAR_Y + BAR_HEIGHT + 20} ${px(marker)},${BAR_Y + BAR_HEIGHT + 4}`}
      />
    </ChartSvg>
  );
}
